import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Collapse,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import fetchAPI from '../../../../lib/utils/fetch-api'
import {
  vekaQualificationOptions as qualifications,
  vekaFinalQualificationOptions as finalQualifications,
} from '../../veka-qualification/options'

interface Rule {
  status: string
  qualification?: string
  final_qualification?: string
}
interface Settings {
  import_enabled: boolean
  export_enabled: boolean
  start_date: string
  rules: Record<string, Rule>
  condition_rules: { attributes: Record<string, Rule>; cancellations: Record<string, Rule | null> }
}
interface RequestRow {
  external_id: string
  partner: string
  point_name: string
  appeal_id: number | null
  import_status: string
  import_error: string | null
  outbound_status: string
  outbound_error: string | null
  outbound_payload: (Rule & { comment: string; sale_amount?: string }) | null
  synced_at: string | null
}
interface State {
  settings: Settings
  requests: RequestRow[]
  key_configured: boolean
  export_available: boolean
  qualification_options: { id: number; value: string; code: string }[]
}

const stages: Record<string, string> = {
  new: 'Новое обращение',
  work: 'Консультация',
  measurement: 'Замер',
  contract: 'Договор',
  manufacture: 'Производство',
  delivery: 'Доставка',
  mounting: 'Монтаж',
  done: 'Выполнено',
  cancelled: 'Отменено',
}
const statuses = [{ value: 'processed', label: 'Обработана' }]
const saleAmounts = [
  { value: 'price_range_a', label: 'До 100 тыс. ₽' },
  { value: 'price_range_b', label: '100–300 тыс. ₽' },
  { value: 'price_range_c', label: 'Более 300 тыс. ₽' },
]
const importLabels: Record<string, string> = {
  received: 'Получена',
  imported: 'В ERP',
  error: 'Ошибка импорта',
}
const exportLabels: Record<string, string> = {
  idle: 'Не подготовлена',
  pending: 'Ожидает отправки',
  sent: 'Отправлена',
  error: 'Ошибка отправки',
  blocked: 'Требует проверки',
  rejected: 'Отклонена VEKA',
}
const dateLabel = (value: string | null) => (value ? new Date(value).toLocaleString('ru-RU') : '—')

const VekaSettings = ({ onSaved }: { onSaved?: () => void } = {}) => {
  const [state, setState] = useState<State | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<RequestRow | null>(null)
  const request = async (path = '', body?: unknown) => {
    const response = await fetchAPI(
      `/api/admin/veka${path}`,
      body === undefined
        ? {}
        : { method: 'POST', body: JSON.stringify(body), timeoutMs: 10 * 60 * 1000 },
    )
    if (response.errors || response.error)
      throw new Error(response.errors?.join(', ') || response.error?.message || 'Ошибка VEKA')
    return response
  }
  const load = useCallback(async () => {
    try {
      setState((await request()) as State)
      setDirty(false)
      setError('')
    } catch (e: any) {
      setError(e.message || 'Не удалось загрузить настройки VEKA')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])
  const updateSettings = (patch: Partial<Settings>) => {
    setState(s => (s ? { ...s, settings: { ...s.settings, ...patch } } : s))
    setDirty(true)
  }
  const conditionTable = (kind: 'attributes' | 'cancellations') => {
    if (!state) return null
    const conditions = state.settings.condition_rules
    const update = (id: number, rule: Rule | null | undefined) => {
      const entries = { ...conditions[kind] }
      if (rule === undefined) delete entries[id]
      else entries[id] = rule
      updateSettings({ condition_rules: { ...conditions, [kind]: entries } })
    }
    return (
      <Table
        rowKey="id"
        pagination={false}
        scroll={{ x: 800 }}
        dataSource={state.qualification_options.filter(
          option =>
            option.code === (kind === 'attributes' ? 'case_order_attribute' : 'case_cancel_reason'),
        )}
        columns={[
          {
            title: kind === 'attributes' ? 'Признак заказа' : 'Причина отказа',
            dataIndex: 'value',
          },
          {
            title: 'Первичное качество VEKA',
            render: (_, row) => {
              const rule = conditions[kind][row.id]
              return (
                <Select
                  style={{ width: 290 }}
                  disabled={busy}
                  aria-label={`Качество VEKA для ${kind} ${row.id}`}
                  value={
                    rule === null
                      ? '__manual'
                      : rule
                      ? rule.qualification || '__preserve'
                      : '__stage'
                  }
                  options={[
                    { value: '__stage', label: 'По правилу этапа' },
                    ...(kind === 'cancellations'
                      ? [
                          { value: '__manual', label: 'Не отправлять до настройки правила' },
                          { value: '__preserve', label: 'Сохранить качество VEKA' },
                        ]
                      : []),
                    ...qualifications,
                  ]}
                  onChange={value =>
                    update(
                      row.id,
                      value === '__stage'
                        ? undefined
                        : value === '__manual'
                        ? null
                        : value === '__preserve'
                        ? { status: 'processed', final_qualification: 'failed' }
                        : {
                            status: 'processed',
                            qualification: value,
                            ...(kind === 'cancellations'
                              ? {
                                  final_qualification:
                                    value === 'missed'
                                      ? 'missed'
                                      : value === 'sell'
                                      ? 'sell'
                                      : 'failed',
                                }
                              : {}),
                          },
                    )
                  }
                />
              )
            },
          },
          {
            title: 'Итоговое качество VEKA',
            render: (_, row) => {
              const rule = conditions[kind][row.id]
              return (
                <Select
                  style={{ width: 210 }}
                  allowClear
                  placeholder="Не передавать"
                  aria-label={`Итоговое качество VEKA для ${kind} ${row.id}`}
                  options={finalQualifications}
                  value={rule?.final_qualification}
                  disabled={busy || !rule}
                  onChange={value =>
                    rule && update(row.id, { ...rule, final_qualification: value || undefined })
                  }
                />
              )
            },
          },
        ]}
      />
    )
  }
  const action = async (path: string) => {
    if (!state) return
    setBusy(true)
    try {
      const result = await request(path, path === '/settings' ? state.settings : {})
      if (path === '/import') {
        const text = `Создано: ${result.created}; повторных обращений: ${result.repeated}; уже загружено: ${result.skipped}; ошибок: ${result.errors.length}`
        result.errors.length ? message.warning(text) : message.success(text)
      } else if (path === '/prepare-export')
        message.success(`Подготовлено обновлений: ${result.queued}`)
      else if (path === '/export')
        message.info(`Отправлено: ${result.sent}; ошибок: ${result.errors}`)
      else message.success('Настройки VEKA сохранены')
      await load()
      onSaved?.()
    } catch (e: any) {
      message.error(e.message || 'Не удалось выполнить обмен VEKA')
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      {loading ? (
        <Spin />
      ) : error ? (
        <Alert type="error" message={error} action={<Button onClick={load}>Повторить</Button>} />
      ) : (
        state && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              type={state.key_configured ? 'info' : 'warning'}
              message={
                state.key_configured ? 'Ключ VEKA настроен' : 'Ключ VEKA ещё не настроен на сервере'
              }
              description="Сопоставления точек настраиваются во вкладке VEKA карточки офиса. Эти параметры обмена действуют для всех офисов. Получение заявок выполняется каждые две минуты. VEKA может отмечать полученные через API заявки обработанными ещё до работы менеджера."
            />
            {!state.export_available && (
              <Alert
                type="info"
                message="Обратная отправка пока недоступна"
                description="Результаты можно подготовить и проверить в ERP. Отправка станет доступна после включения обратного обмена администратором сервера."
              />
            )}
            <Space wrap size="large">
              <Space>
                <Typography.Text>Получать заявки</Typography.Text>
                <Switch
                  checked={state.settings.import_enabled}
                  disabled={busy || !state.key_configured}
                  onChange={v => updateSettings({ import_enabled: v })}
                />
              </Space>
              <Space>
                <Typography.Text>Отправлять результаты</Typography.Text>
                <Switch
                  checked={state.settings.export_enabled}
                  disabled={busy || !state.export_available}
                  onChange={v => updateSettings({ export_enabled: v })}
                />
              </Space>
              <Space>
                <Typography.Text>Начать с</Typography.Text>
                <Input
                  type="date"
                  value={state.settings.start_date}
                  disabled={busy}
                  onChange={e => updateSettings({ start_date: e.target.value })}
                />
              </Space>
              <Button
                type="primary"
                disabled={!dirty}
                loading={busy}
                onClick={() => action('/settings')}
              >
                Сохранить
              </Button>
            </Space>
            <Collapse
              items={[
                {
                  key: 'rules',
                  label: 'Сопоставление этапов ERP со статусами VEKA',
                  children: (
                    <>
                      <Typography.Paragraph>
                        Этап, причина отказа и точная сумма передаются в заметках. Для продажи
                        дополнительно передаётся диапазон суммы. Продажа и отказ отражаются в
                        итоговом качестве; обычный отказ не относится к спаму. Записанное итоговое
                        качество и диапазон суммы API не очищает при передаче пустого значения.
                      </Typography.Paragraph>
                      <Table
                        rowKey="code"
                        pagination={false}
                        dataSource={Object.entries(state.settings.rules).map(([code, rule]) => ({
                          code,
                          ...rule,
                        }))}
                        columns={[
                          { title: 'Этап ERP', render: (_, r) => stages[r.code] || r.code },
                          {
                            title: 'Статус VEKA',
                            render: (_, r) => (
                              <Select
                                aria-label={`Статус VEKA для ${r.code}`}
                                style={{ width: 210 }}
                                options={statuses}
                                value={r.status}
                                disabled={busy}
                                onChange={v =>
                                  updateSettings({
                                    rules: {
                                      ...state.settings.rules,
                                      [r.code]: { ...state.settings.rules[r.code], status: v },
                                    },
                                  })
                                }
                              />
                            ),
                          },
                          {
                            title: 'Квалификация VEKA',
                            render: (_, r) => (
                              <Select
                                aria-label={`Квалификация VEKA для ${r.code}`}
                                style={{ width: 220 }}
                                options={qualifications}
                                value={r.qualification}
                                allowClear={r.code === 'cancelled'}
                                placeholder="Сохранить качество VEKA"
                                disabled={busy}
                                onChange={v =>
                                  updateSettings({
                                    rules: {
                                      ...state.settings.rules,
                                      [r.code]: {
                                        ...state.settings.rules[r.code],
                                        qualification: v || undefined,
                                      },
                                    },
                                  })
                                }
                              />
                            ),
                          },
                          {
                            title: 'Итоговое качество VEKA',
                            render: (_, r) => (
                              <Select
                                aria-label={`Итоговое качество VEKA для ${r.code}`}
                                style={{ width: 220 }}
                                options={finalQualifications}
                                value={r.final_qualification}
                                placeholder="Не передавать"
                                allowClear
                                disabled={busy}
                                onChange={v =>
                                  updateSettings({
                                    rules: {
                                      ...state.settings.rules,
                                      [r.code]: {
                                        ...state.settings.rules[r.code],
                                        final_qualification: v || undefined,
                                      },
                                    },
                                  })
                                }
                              />
                            ),
                          },
                        ]}
                      />
                    </>
                  ),
                },
                {
                  key: 'attributes',
                  label: 'Качество по признаку заказа',
                  children: (
                    <>
                      <Typography.Paragraph>
                        Ремонт и комплектующие передаются как сервисное обращение. Правила признака
                        действуют до этапов продажи; правила продажи имеют приоритет.
                      </Typography.Paragraph>
                      {conditionTable('attributes')}
                    </>
                  ),
                },
                {
                  key: 'cancellations',
                  label: 'Качество по причине отказа',
                  children: (
                    <>
                      <Typography.Paragraph>
                        Соответствия причин и качества настраиваются один раз и применяются
                        автоматически. Для несогласованной причины выберите «Не отправлять до
                        настройки правила». Обычный отказ по цене не считается спамом.
                      </Typography.Paragraph>
                      {conditionTable('cancellations')}
                    </>
                  ),
                },
              ]}
            />
            <Space wrap>
              <Button
                disabled={busy || dirty || !state.settings.import_enabled}
                onClick={() => action('/import')}
              >
                Получить заявки сейчас
              </Button>
              <Button disabled={busy || dirty} onClick={() => action('/prepare-export')}>
                Подготовить результаты
              </Button>
              <Button
                disabled={
                  busy || dirty || !state.export_available || !state.settings.export_enabled
                }
                onClick={() => action('/export')}
              >
                Отправить результаты
              </Button>
              <Button disabled={busy || dirty} onClick={load}>
                Обновить журнал
              </Button>
            </Space>
            <Typography.Title level={4}>Последние 100 заявок</Typography.Title>
            <Table<RequestRow>
              rowKey="external_id"
              dataSource={state.requests}
              scroll={{ x: 1000 }}
              pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Номер VEKA', dataIndex: 'external_id' },
                { title: 'Партнёр', dataIndex: 'partner' },
                { title: 'Точка', dataIndex: 'point_name' },
                {
                  title: 'Обращение ERP',
                  render: (_, r) =>
                    r.appeal_id ? <a href={`/appeal/${r.appeal_id}`}>№{r.appeal_id}</a> : '—',
                },
                {
                  title: 'Получение',
                  render: (_, r) => (
                    <>
                      <Tag
                        color={
                          r.import_status === 'error'
                            ? 'red'
                            : r.import_status === 'imported'
                            ? 'green'
                            : 'default'
                        }
                      >
                        {importLabels[r.import_status] || r.import_status}
                      </Tag>
                      {r.import_error && <div>{r.import_error}</div>}
                    </>
                  ),
                },
                {
                  title: 'Обратная отправка',
                  render: (_, r) => (
                    <>
                      <Tag
                        color={
                          ['blocked', 'rejected', 'error'].includes(r.outbound_status)
                            ? 'red'
                            : r.outbound_status === 'sent'
                            ? 'green'
                            : 'default'
                        }
                      >
                        {exportLabels[r.outbound_status] || r.outbound_status}
                      </Tag>
                      {r.outbound_error && <div>{r.outbound_error}</div>}
                      {r.synced_at && <div>{dateLabel(r.synced_at)}</div>}
                      {r.outbound_payload && (
                        <Button type="link" onClick={() => setPreview(r)} style={{ padding: 0 }}>
                          Посмотреть результат
                        </Button>
                      )}
                    </>
                  ),
                },
              ]}
            />
            <Modal
              title={`Результат для VEKA ${preview?.external_id || ''}`}
              open={Boolean(preview)}
              onCancel={() => setPreview(null)}
              footer={<Button onClick={() => setPreview(null)}>Закрыть</Button>}
            >
              <Typography.Paragraph>
                Статус: {statuses.find(s => s.value === preview?.outbound_payload?.status)?.label}
                <br />
                Квалификация:{' '}
                {qualifications.find(q => q.value === preview?.outbound_payload?.qualification)
                  ?.label || 'Не изменяется'}
                <br />
                Итоговое качество:{' '}
                {finalQualifications.find(
                  q => q.value === preview?.outbound_payload?.final_qualification,
                )?.label || 'Не передаётся'}
                <br />
                Диапазон суммы:{' '}
                {saleAmounts.find(q => q.value === preview?.outbound_payload?.sale_amount)?.label ||
                  'Не передаётся'}
              </Typography.Paragraph>
              <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                {preview?.outbound_payload?.comment}
              </Typography.Paragraph>
            </Modal>
          </Space>
        )
      )}
    </>
  )
}

export default VekaSettings
